import React from 'react';
import { Clock, LogOut, RefreshCw } from 'lucide-react';

/**
 * SessionTimeoutModal - Warning modal shown N minutes before auto-logout.
 *
 * Props:
 *   - open: boolean - whether modal is visible
 *   - secondsRemaining: number - seconds left until logout
 *   - onExtend: () => void - user clicked "Stay Signed In"
 *   - onLogout: () => void - user clicked "Log Out Now"
 */

const formatTime = (seconds) => {
  const m = Math.floor(Math.max(seconds, 0) / 60);
  const s = Math.max(seconds, 0) % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

export const SessionTimeoutModal = ({ open, secondsRemaining, onExtend, onLogout }) => {
  if (!open) return null;

  return (
    <div
      style={{
        // Above the guided tour (10000) — the session-expiry warning must never
        // be buried under another overlay.
        position: 'fixed', inset: 0, zIndex: 10010,
        background: 'rgba(15, 23, 42, 0.55)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-timeout-title"
    >
      <div
        style={{
          background: '#fff', borderRadius: 20,
          width: '100%', maxWidth: 440,
          boxShadow: '0 25px 80px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.05)',
          overflow: 'hidden',
          animation: 'slideUp 0.3s cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        {/* Gradient header */}
        <div style={{
          background: 'linear-gradient(135deg, #F59E0B, #D97706)',
          padding: '28px 32px 24px', textAlign: 'center', color: '#fff',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{
              width: 56, height: 56, borderRadius: 16,
              background: 'rgba(255,255,255,0.2)',
              margin: '0 auto 12px', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              backdropFilter: 'blur(4px)',
            }}>
              <Clock size={28} color="#fff" strokeWidth={2} />
            </div>
            <div id="session-timeout-title" style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.3px' }}>
              Session Expiring Soon
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '24px 32px 28px' }}>
          <p style={{ margin: 0, fontSize: 14, color: '#6B7280', lineHeight: 1.5, textAlign: 'center' }}>
            For your security, you'll be automatically logged out due to session time out. Any unsaved changes may be lost.
          </p>

          {/* Countdown */}
          <div style={{
            margin: '20px auto', padding: 16,
            borderRadius: 14, textAlign: 'center',
            background: 'rgba(245, 158, 11, 0.08)',
            border: '1.5px solid rgba(245, 158, 11, 0.25)',
            maxWidth: 280,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#92400E', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
              Logging out in
            </div>
            <div style={{
              fontSize: 40, fontWeight: 800, color: '#D97706',
              fontVariantNumeric: 'tabular-nums', letterSpacing: '-1px', lineHeight: 1,
            }}>
              {formatTime(secondsRemaining)}
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button
              onClick={onLogout}
              style={{
                flex: 1, padding: '12px 16px', borderRadius: 12,
                border: '1.5px solid #E8ECF0', background: '#fff',
                color: '#374151', fontWeight: 600, fontSize: 14,
                cursor: 'pointer', fontFamily: 'inherit',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#F9FAFB'; e.currentTarget.style.borderColor = '#D1D5DB'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#E8ECF0'; }}
            >
              <LogOut size={15} strokeWidth={2} />
              Log Out Now
            </button>
            <button
              onClick={onExtend}
              style={{
                flex: 1, padding: '12px 16px', borderRadius: 12,
                border: 'none',
                background: 'linear-gradient(135deg, #0D9488, #0F766E)',
                color: '#fff', fontWeight: 700, fontSize: 14,
                cursor: 'pointer', fontFamily: 'inherit',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                boxShadow: '0 4px 12px rgba(13, 148, 136, 0.3)',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(13, 148, 136, 0.4)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(13, 148, 136, 0.3)'; }}
            >
              <RefreshCw size={15} strokeWidth={2.5} />
              Stay Signed In
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeoutModal;
